'use client';

import React from 'react';

function StarIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-400 fill-yellow-400" aria-hidden="true">
      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
    </svg>);

}

interface TestimonialCardProps {
  metricValue: string;
  metricLabel: string;
  quote: string;
  avatarSrc: string;
  name: string;
  role: string;
  delay: string;
}

function TestimonialCard({ metricValue, metricLabel, quote, avatarSrc, name, role, delay }: TestimonialCardProps) {
  return (
    <div className={`reveal ${delay} testimonial-card glass-card rounded-3xl p-7 flex flex-col`}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
        </div>
        <div className="text-right">
          <div className="text-sm font-black gradient-text">{metricValue}</div>
          <div className="text-xs text-neutral-600">{metricLabel}</div>
        </div>
      </div>
      <blockquote className="text-sm text-neutral-300 leading-relaxed mb-6 flex-1" style={{ fontStyle: 'normal' }}>
        {quote}
      </blockquote>
      <div className="flex items-center gap-3 pt-5 border-t border-white/[0.06]">
        <img
          alt={name}
          className="w-10 h-10 rounded-full object-cover"
          src={avatarSrc}
          style={{ border: '1px solid rgba(59, 130, 246, 0.25)' }} />
        
        <div>
          <div className="text-sm font-semibold text-white">{name}</div>
          <div className="text-xs text-neutral-500">{role}</div>
        </div>
      </div>
    </div>);

}

export default function TestimonialsSection() {
  const testimonials = [
  {
    metricValue: '$18K/month',
    metricLabel: 'Monthly Revenue',
    quote: '"Scalio completely transformed my content strategy. We went from inconsistent posting to a fully automated system generating $18K/month. Worth every penny and then some."',
    avatarSrc: 'https://images.unsplash.com/photo-1769636929231-3cd7f853d038?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc1MjA1ODU1fDA&ixlib=rb-4.1.0&q=85',
    name: 'Sarah K.',
    role: 'Content Creator',
    delay: 'reveal-delay-1'
  },
  {
    metricValue: '3x Reach',
    metricLabel: 'In 60 Days',
    quote: '"Within 60 days of working with Scalio, our content reach tripled and we saved 30+ hours per week. Their AI workflows are unlike anything else on the market right now."',
    avatarSrc: 'https://images.unsplash.com/photo-1758534063829-a72058381e21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc1MjA1ODU1fDA&ixlib=rb-4.1.0&q=85',
    name: 'Marcus T.',
    role: 'Founder, TechStartup',
    delay: 'reveal-delay-2'
  },
  {
    metricValue: '500K+ Views',
    metricLabel: 'Month One',
    quote: '"From 0 to 500K views in our first month of working together. The AI content system Scalio built is the single best investment we\'ve made for our brand — bar none."',
    avatarSrc: 'https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc1MjA1ODU1fDA&ixlib=rb-4.1.0&q=85',
    name: 'Aisha R.',
    role: 'E-commerce Brand Owner',
    delay: 'reveal-delay-3'
  }];


  return (
    <section
      className="relative py-24 sm:py-32 overflow-hidden"
      style={{ background: 'rgb(5, 5, 5)' }}>
      
      {/* Background orb */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-end pr-10">
        <div style={{ width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(157, 76, 221, 0.07) 0%, transparent 70%)', filter: 'blur(50px)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="reveal text-center mb-16">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-4 block">Client Love</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4" style={{ letterSpacing: '-0.02em' }}>What Our Clients Say</h2>
          <p className="text-base text-neutral-500 max-w-md mx-auto">Real stories from real brands who scaled with AI.</p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t) =>
          <TestimonialCard key={t.name} {...t} />
          )}
        </div>
      </div>
    </section>);

}